// What a person may do on one website: per area, none, view or edit (owners: everything, everywhere). The same model as
// a website's admin API (tasma-website src/server/domain/permissions.ts): the hub writes it into each site token, the
// website checks it. Keep the two lists in step.

export const AREAS = ["content", "media", "leads", "stats", "scores", "history", "settings"] as const;
export type Area = (typeof AREAS)[number];
export type Level = "none" | "view" | "edit";
export type Grant = Partial<Record<Area, Level>>;

/** Each area's name on the screens. What it covers differs per kind of website: shared/sites.ts KIND_AREAS. */
export const AREA_LABELS: Record<Area, { name: string }> = {
  content: { name: "Content" },
  media: { name: "Media" },
  leads: { name: "Leads" },
  stats: { name: "Statistics" },
  scores: { name: "Game scores" },
  history: { name: "History" },
  settings: { name: "Settings & publish" },
};

const RANK: Record<Level, number> = { none: 0, view: 1, edit: 2 };

/** Whether a grant allows `level` in `area` (edit includes view). Owners pass `owner: true`. */
export function allows(owner: boolean, grant: Grant, area: Area, level: Exclude<Level, "none">) {
  return owner || RANK[grant[area] ?? "none"] >= RANK[level];
}

/** A grant read from anywhere (the database, a request): only known areas, only view or edit. */
export function cleanGrant(input: unknown): Grant {
  const raw = input && typeof input === "object" ? (input as Record<string, unknown>) : {};
  const grant: Grant = {};
  for (const area of AREAS) {
    const level = Object.hasOwn(raw, area) ? raw[area] : undefined;
    if (level === "view" || level === "edit") grant[area] = level;
  }
  return grant;
}

/** Whether a grant gives anything at all (a member with an empty grant doesn't see the site). */
export const anyAccess = (grant: Grant) => Object.values(grant).some((l) => l === "view" || l === "edit");
